import { useState, type ReactNode } from 'react';
import { ApiError } from '../../api';

/**
 * TICK-129 — the parts every Operations screen was writing for itself.
 *
 * Six panels shipped in six days, each with its own copy of: read the message off an `ApiError`,
 * wrap a write so the list reloads after it, render "loading" and "this failed", and lay out the
 * buttons at the end of a row. Copies drift, and these already had:
 *
 * - the version conflict (`OPS_VERSION_CONFLICT`) reached the screen as an explanation in the crew
 *   panel and as the server's bare sentence in the other four, even though all five hit the same
 *   409 from the same rule;
 * - the dependency cycle was turned into task names in one place only, because that is where it was
 *   noticed.
 *
 * Consolidating them is not tidying: it gives every panel the best behaviour any of them had. The
 * proof it changed nothing else is that the eleven Operations e2e tests pass untouched.
 */

/** The server's own sentence is almost always the useful one. This is the fallback, not the answer. */
export function opsErrorText(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

type OpsErrorBody = { code?: string; entity?: string; current?: unknown; path?: unknown } | undefined;

const bodyOf = (e: unknown): OpsErrorBody => (e instanceof ApiError ? (e.body as OpsErrorBody) : undefined);

/** True when this write lost a race — the caller reloads so the screen shows what won. */
export const isVersionConflict = (e: unknown) => bodyOf(e)?.code === 'OPS_VERSION_CONFLICT';

export interface OpsPanelOptions {
  /**
   * Turns record ids inside an error body into names this screen can print.
   *
   * The dependency cycle is the case that needs it: the server answers with the path that closes the
   * loop as a list of ids, and only the screen holding the tasks can say "A → B → A". Without it the
   * operator is told a loop exists and left to find it.
   */
  describeIds?: (ids: string[]) => string[];
}

export interface OpsPanel {
  busy: boolean;
  error: string;
  setError: (message: string) => void;
  /**
   * Runs a write, reloads, and reports what happened. Returns true when the write went through, so a
   * caller can close its editor only on success.
   */
  run: (action: () => Promise<unknown>) => Promise<boolean>;
}

export function useOpsPanel(
  reload: () => Promise<void>,
  { describeIds }: OpsPanelOptions = {},
): OpsPanel {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const explain = (e: unknown): string => {
    const body = bodyOf(e);
    if (body?.code === 'OPS_TASK_DEPENDENCY_CYCLE' && Array.isArray(body.path)) {
      const ids = body.path as string[];
      const names = describeIds ? describeIds(ids) : ids;
      return `${opsErrorText(e, 'บันทึกไม่สำเร็จ')} — ${names.join(' → ')}`;
    }
    if (body?.code === 'OPS_VERSION_CONFLICT') {
      return 'มีคนแก้ไขข้อมูลนี้ไปก่อนแล้ว — โหลดค่าล่าสุดให้แล้ว ตรวจแล้วลองใหม่อีกครั้ง';
    }
    return opsErrorText(e, 'บันทึกไม่สำเร็จ');
  };

  const run = async (action: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError('');
    try {
      await action();
      await reload();
      return true;
    } catch (e) {
      setError(explain(e));
      // A conflict is the one failure where the screen is now wrong as well as unsaved.
      if (isVersionConflict(e)) await reload();
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, setError, run };
}

/**
 * What a panel renders before its first load finishes, or when that load failed.
 *
 * Deliberately not a wrapper that takes the whole panel as children: the caller keeps its own
 * `if (!data) return …`, which is what tells TypeScript the data is there for the rest of the
 * function. A gate component would hide that check at runtime and lose the narrowing, trading a
 * real guarantee for two fewer lines.
 */
export function OpsPanelState({ error }: { error: string }) {
  if (error) return <div className="form-error">{error}</div>;
  return <div style={{ color: 'var(--dim)', fontSize: 13 }}>กำลังโหลด…</div>;
}

/** The buttons at the end of a table row, spaced so "แก้ไข" and "ลบ" are not one target. */
export function RowActions({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
      {children}
    </div>
  );
}

/** A table that scrolls inside itself rather than pushing the page sideways on a narrow screen. */
export function OpsTable({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead><tr>{head}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
